import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import './AccountHeadModal.css';

const CapitalModal = ({
  open,
  onClose,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  balanceSheetHeads = [],
  loading
}) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      setFormData(selectedItem);
    } else if (modalAction === 'create') {
      setFormData(getDefaultFormData());
    }
  }, [selectedItem, modalAction, setFormData]);

  const getDefaultFormData = () => {
    // Get current date in YYYY-MM-DD format
    const currentDate = new Date().toISOString().split('T')[0];
    
    return {
      type: 'capital',
      Name: '',
      Description: '',
      SearchableName: '',
      BalanceSheetHeadId: null,
      OpeningBalance: 0,
      OpeningDate: currentDate,
      IsPayableOrReceivable: false,
      IsActive: true
    };
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Helper to get value from either lowercase or PascalCase field
    const getValue = (lowercase, pascalCase) => formData[lowercase] ?? formData[pascalCase];
    
    if (!(getValue('name', 'Name')?.toString().trim())) {
      newErrors.name = 'Capital name is required';
    }
    
    if (!(getValue('balanceSheetHeadId', 'BalanceSheetHeadId')?.toString().trim())) {
      newErrors.balanceSheetHeadId = 'Balance sheet head is required';
    }
    
    const openingBalance = getValue('openingBalance', 'OpeningBalance');
    if (openingBalance && openingBalance < 0) {
      newErrors.openingBalance = 'Opening balance cannot be negative';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-generate searchable name from name
    if (field === 'Name') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        SearchableName: value?.toLowerCase().replace(/[^a-z0-9]/g, '') || ''
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave();
    }
  };

  const getModalTitle = () => {
    const actionNames = {
      create: 'New',
      edit: 'Edit',
      view: 'View'
    };
    
    const actionName = actionNames[modalAction] || 'New';
    
    return {
      title: `${actionName} Capital`,
      subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} capital ${modalAction === 'create' ? 'to your accounts' : 'details'}`
    };
  };

  const renderForm = () => (
    <div className="form-container">
      {/* Basic Information Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 1 }}>
        Basic Information
      </Typography>
      
      <div className="form-row">
        <div className="form-field required-field">
          <TextField
            fullWidth
            label="Capital Name *"
            placeholder="Enter capital name"
            value={formData.Name || formData.name || ''}
            onChange={(e) => handleInputChange('Name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            disabled={modalAction === 'view'}
            required
          />
        </div>
        <div className="form-field">
          <TextField
            fullWidth
            label="Searchable Name"
            placeholder="Auto-generated from name"
            value={formData.SearchableName || formData.searchableName || ''}
            onChange={(e) => handleInputChange('SearchableName', e.target.value)}
            disabled={modalAction === 'view'}
            helperText="Used for quick search (auto-generated)"
          />
        </div>
      </div>
      
      {/* Classification Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        Classification
      </Typography>
      
      <div className="form-row">
        <div className="form-field">
          <FormControl fullWidth error={!!errors.balanceSheetHeadId}>
            <InputLabel>Balance Sheet Head *</InputLabel>
            <Select
              value={formData.BalanceSheetHeadId || formData.balanceSheetHeadId || ''}
              onChange={(e) => handleInputChange('BalanceSheetHeadId', e.target.value)}
              disabled={modalAction === 'view'}
              label="Balance Sheet Head *"
            >
              <MenuItem value="">Select Balance Sheet Head</MenuItem>
              {balanceSheetHeads.map(head => (
                <MenuItem key={head.Id || head.id} value={head.Id || head.id}>
                  {head.Name || head.name}
                </MenuItem>
              ))}
            </Select>
            {errors.balanceSheetHeadId && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.balanceSheetHeadId}</Typography>}
          </FormControl>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsPayableOrReceivable ?? formData.isPayableOrReceivable ?? false}
                onChange={(e) => handleInputChange('IsPayableOrReceivable', e.target.checked)}
                disabled={modalAction === 'view'}
              />
            }
            label="Payable/Receivable"
          />
        </div>
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsActive ?? formData.isActive ?? true}
                onChange={(e) => handleInputChange('IsActive', e.target.checked)}
                disabled={modalAction === 'view'}
              />
            }
            label="Active"
          />
        </div>
      </div>

      {/* Financial Information Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        Financial Information
      </Typography>
      
      <div className="form-row">
        <div className="form-field">
          <TextField
            fullWidth
            label="Opening Balance (₹)"
            placeholder="0.00"
            type="number"
            value={formData.OpeningBalance || formData.openingBalance || ''}
            onChange={(e) => handleInputChange('OpeningBalance', parseFloat(e.target.value) || 0)}
            error={!!errors.openingBalance}
            helperText={errors.openingBalance}
            disabled={modalAction === 'view'}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </div>
        <div className="form-field">
          <TextField
            fullWidth
            label="Opening Date"
            type="date"
            value={formData.OpeningDate ? formData.OpeningDate.split('T')[0] : formData.openingDate || ''}
            onChange={(e) => handleInputChange('OpeningDate', e.target.value)}
            disabled={modalAction === 'view'}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </div>
      </div>

      {/* Description Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        Additional Details
      </Typography>
      
      <div className="form-field">
        <TextField
          fullWidth
          label="Description"
          placeholder="Enter capital description (optional, max 150 chars)"
          multiline
          rows={2}
          value={formData.Description || formData.description || ''}
          onChange={(e) => handleInputChange('Description', e.target.value)}
          disabled={modalAction === 'view'}
          inputProps={{ maxLength: 150 }}
          helperText={`${(formData.Description || formData.description || '').length}/150 characters`}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      className="account-head-modal"
    >
      <DialogTitle>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h2>{getModalTitle().title}</h2>
          </div>
          <Button
            onClick={onClose}
            className="modal-close-btn"
          >
            <Close />
          </Button>
        </div>
      </DialogTitle>
      
      <DialogContent>
        {renderForm()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {modalAction !== 'view' && (
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {modalAction === 'create' ? 'Add Capital' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CapitalModal;